import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'

/**
 * The admin Reporting surface data hooks.
 *
 * The catalogue (GET /api/reports/) and a report run (GET /api/reports/<key>/)
 * are both fully generic: the backend serves whatever the tenant's report
 * definitions describe (columns, filters), so the section renders zero
 * hardcoded report/column names. Finance reports are omitted from the catalogue
 * for actors without `reports.view_finance` (server-side).
 */

export interface ReportColumnMeta {
  key: string
  label: string
}

export interface ReportFilterOption {
  value: string
  label: string
}

export interface ReportFilterSpec {
  key: string
  label: string
  type: 'date' | 'select' | 'text'
  query?: string
  options?: ReportFilterOption[]
}

export interface ReportSummary {
  key: string
  title: string
  description: string
  source: string
  requires_finance: boolean
  filters: ReportFilterSpec[]
  columns: ReportColumnMeta[]
  can_export: boolean
}

export interface ReportRunResult {
  columns: ReportColumnMeta[]
  count: number
  page: number
  page_size: number
  results: Array<Record<string, unknown>>
}

/** The tenant's report catalogue (config-time data; revalidate on mount). */
export function useReports() {
  return useQuery<ReportSummary[]>({
    queryKey: ['reports', 'catalog'],
    queryFn: () => api.get('/reports/').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  })
}

/**
 * A single report's rows for the in-modal preview. Disabled until a report is
 * selected so the list view fires no run queries.
 */
export function useReportPreview(
  key: string | null,
  params: Record<string, string>,
  enabled: boolean,
) {
  return useQuery<ReportRunResult>({
    queryKey: ['reports', 'run', key, params],
    queryFn: () =>
      api.get(`/reports/${key}/`, { params }).then((r) => r.data),
    enabled: Boolean(key) && enabled,
    staleTime: 30 * 1000,
  })
}
